import { groupBy, mapValues } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';
import { Draft, produce } from 'immer';

import { BearingEnvelope, CeilingPlane, RoofPlane } from '@paragon/contract-library';
import { Polygon2D, Polygon3D } from '@paragon/geometry-library';
import {
  calculateAbsoluteVerticalCutPlaneGeometry,
  calculateBearingEnvelopeShape,
  calculateBoundedPlane,
  calculateCutPlaneAtBaseLine,
  convertPlaneToGeometricPlane,
  createDisjointRoofBasePolygonFromEdges,
  DEFAULT_HEEL_SETTINGS,
  DisplayablePlane,
  DisplayablePlaneWithCalculatedGeometry,
  getPlaneWithGeometry,
  planeCutToPlane,
  PlaneWithCalculatedGeometry,
} from '@paragon/layout';
import { assertExhaustiveSwitch, groupTransitively, notNullish, ReadonlyRecord } from '@paragon/utilities';

import { RootState } from '../index';
import {
  isInsertionCeilingPlaneFullyDefined,
  isInsertionRoofPlaneFullyDefined,
  PickingPlaneInfo,
} from '../slices/applicationSlice';
import { getFeaturePatternContainerShapes } from './featurePatternContainerSelectors';
import { getInsertingObjectInfo } from './insertionObjectSelectors';
import {
  getActiveLayoutLevelGuidOrNull,
  getAllBearingEnvelopes,
  getVisibleBearingEnvelopes,
  getVisibleCeilingPlaneGuids,
  getVisibleLevelGuids,
  getVisibleRoofPlaneGuids,
  NULL_LEVEL_GUID_KEY,
} from './layoutDataSelectors';
import { getPickingObjectInfo } from './pickingSelectors';

const getHighlightedAbsoluteVerticalPlane = (state: RootState) =>
  state.layout.highlightedPlane?.type === 'absoluteVertical' ? state.layout.highlightedPlane : null;

export const getInsertionRoofPlane = createSelector(getInsertingObjectInfo, (insertingObjectInfo) => {
  if (insertingObjectInfo?.type === 'roofPlane') {
    return insertingObjectInfo.data;
  }
});

export const getInsertionCeilingPlane = createSelector(getInsertingObjectInfo, (insertingObjectInfo) => {
  if (insertingObjectInfo?.type === 'ceilingPlane') {
    return insertingObjectInfo.data;
  }
});

const getCalculatedInsertionPlane = createSelector(
  getInsertionRoofPlane,
  getInsertionCeilingPlane,
  (state: RootState) => state.layout.bearingEnvelopes,

  (roofPlane, ceilingPlane, bearingEnvelopes): PlaneWithCalculatedGeometry | null => {
    if (roofPlane && isInsertionRoofPlaneFullyDefined(roofPlane)) {
      return getPlaneWithGeometry(roofPlane, bearingEnvelopes);
    }

    if (ceilingPlane && isInsertionCeilingPlaneFullyDefined(ceilingPlane)) {
      return getPlaneWithGeometry(ceilingPlane, bearingEnvelopes);
    }

    return null;
  },
);

export const getPickingPlaneInfo = (state: RootState) => {
  const pickingInfo = getPickingObjectInfo(state);
  if (pickingInfo?.type === 'pickingPlane') return pickingInfo;
};

export const getCalculatedRoofAndCeilingPlanes = createSelector(
  (state: RootState) => state.layout.bearingEnvelopes,
  (state: RootState) => state.layout.roofPlanes,
  (state: RootState) => state.layout.ceilingPlanes,

  (bearingEnvelopes, roofPlanes, ceilingPlanes) =>
    Object.fromEntries(
      ([] as (RoofPlane | CeilingPlane)[])
        .concat(Object.values(roofPlanes), Object.values(ceilingPlanes))
        .map((plane) => [plane.guid, getPlaneWithGeometry(plane, bearingEnvelopes)] as const),
    ),
);

export const getBoundedRoofAndCeilingPlaneShapesByGuid = createSelector(
  getCalculatedRoofAndCeilingPlanes,
  (calculatedPlanesDictionary) =>
    Object.fromEntries(
      Object.entries(calculatedPlanesDictionary).map(
        ([guid, plane]) =>
          [guid, plane ? calculateBoundedPlane(plane, calculatedPlanesDictionary).shape : null] as const,
      ),
    ),
);

/**
 * Calculates the 3D polygons of roof and ceiling shapes in the open layout, grouped by their level.
 *
 * An additional special key 'visible' is always present which contains polygons from all visible levels, and also works
 *   for layouts without any levels.
 */
const getBoundedRoofAndCeilingPlaneShapes = createSelector(
  getVisibleLevelGuids,
  (state: RootState) => state.layout.roofPlanes,
  (state: RootState) => state.layout.ceilingPlanes,
  getBoundedRoofAndCeilingPlaneShapesByGuid,

  (visibleLevels, roofPlanes, ceilingPlanes, shapes) => {
    const calculateShapes = (planes: { guid: string }[]) => planes.flatMap(({ guid }) => shapes[guid] ?? []);

    const visiblePlanes = Object.values(ceilingPlanes)
      .concat(Object.values(roofPlanes))
      .filter((plane) => visibleLevels.has(plane.levelGuid ?? null));

    const calculatedShapesByLevel = Object.entries(
      mapValues(
        groupBy(visiblePlanes.filter(notNullish), (plane) => plane.levelGuid ?? NULL_LEVEL_GUID_KEY),
        calculateShapes,
      ),
    );

    const entries: (readonly [string, Polygon3D[]])[] = [
      ['visible', calculateShapes(visiblePlanes)],
      ...calculatedShapesByLevel,
    ];

    return Object.fromEntries(entries);
  },
);

/**
 * Calculates a roof outline polygon for each currently visible level, based on the bearing envelopes, feature pattern
 * containers, and roof+ceiling shapes on those level(s).
 *
 * An additional special key 'visible' is always present in the returned dictionary, and contains a roof outline created
 *    from all visible levels or the entire layout.
 */
export const getRoofOutlinesFromBearingsFeaturePatternContainersAndPlanes = createSelector(
  getVisibleLevelGuids,

  getAllBearingEnvelopes,
  getVisibleBearingEnvelopes,
  getBoundedRoofAndCeilingPlaneShapes,
  getFeaturePatternContainerShapes,

  (
    visibleLevels,
    unfilteredBearingEnvelopes,
    visibleBearingEnvelopes,
    roofAndCeilingPlaneShapes,
    featurePatternContainerShapes,
  ) => {
    function calculateResult(
      relevantBearings: BearingEnvelope[],
      relevantOther3DShapes: Polygon3D[],
      relevantOther2DShapes: Polygon2D[],
    ) {
      const polygons = relevantBearings
        .map(calculateBearingEnvelopeShape)
        .concat(relevantOther3DShapes.map((shape) => shape.projectTo2D()))
        .concat(relevantOther2DShapes)
        .filter(notNullish);

      return groupTransitively(polygons, (p1, p2) => p1.touchesOrOverlaps(p2)).flatMap((touchingGroup) =>
        createDisjointRoofBasePolygonFromEdges(touchingGroup.flatMap(({ edges }) => edges)),
      );
    }

    return Object.fromEntries(
      Array.from(visibleLevels)
        .map((level) => {
          const relevantBearingEnvelopes = unfilteredBearingEnvelopes.filter(
            (envelope) => envelope.levelGuid === level,
          );
          const relevantOtherShapes = roofAndCeilingPlaneShapes[level ?? NULL_LEVEL_GUID_KEY] ?? [];
          const relevantOther2DShapes = featurePatternContainerShapes[level ?? NULL_LEVEL_GUID_KEY] ?? [];

          return [
            level ?? NULL_LEVEL_GUID_KEY,
            calculateResult(relevantBearingEnvelopes, relevantOtherShapes, relevantOther2DShapes),
          ] as const;
        })
        .concat([
          [
            'visible',
            calculateResult(
              visibleBearingEnvelopes,
              roofAndCeilingPlaneShapes['visible'],
              featurePatternContainerShapes['visible'],
            ),
          ],
        ] as const),
    );
  },
);

export const getCurrentlyPickablePlaneGuids = createSelector(
  getVisibleRoofPlaneGuids,
  getVisibleCeilingPlaneGuids,
  getCalculatedRoofAndCeilingPlanes,

  getPickingObjectInfo,
  (state: RootState) => state.application.insertingObjectInfo,

  (roofPlaneGuids, ceilingPlaneGuids, allPlanes, pickingObjectInfo, insertingObjectInfo): Set<string> => {
    if (pickingObjectInfo?.type !== 'pickingPlane') {
      return new Set();
    }

    const { targetInfo } = pickingObjectInfo;

    // There are no snapping target settings or modes that affect what planes can be picked, so we're just determining
    //   the targetable planes here based on the type of pick being performed. Feel free to make this more complicated
    //   if and when you need to.
    switch (targetInfo.type) {
      case 'ceiling-cut':
        return getPickablePlaneGuidsDuringCutting(ceilingPlaneGuids, allPlanes, targetInfo.planeGuidToCut);

      case 'roof-cut':
        return getPickablePlaneGuidsDuringCutting(roofPlaneGuids, allPlanes, targetInfo.planeGuidToCut);

      case 'fascia-height-reference':
        return roofPlaneGuids;

      case 'corner-set-plane-1':
      case 'corner-set-plane-2':
        const { plane1Guid, plane2Guid } = insertingObjectInfo?.type === 'cornerSet' ? insertingObjectInfo.data : {};
        return new Set(Array.from(roofPlaneGuids).filter((guid) => guid !== plane1Guid && guid !== plane2Guid));

      case 'truss-envelope-top-cut':
      case 'truss-envelope-bottom-cut':
      case 'truss-envelope-ordered-top-cut':
      case 'truss-envelope-ordered-bottom-cut':
        return new Set([...roofPlaneGuids, ...ceilingPlaneGuids]);

      default:
        assertExhaustiveSwitch(targetInfo);
    }
  },
);

export const getPlanesToDisplay = createSelector(
  getCalculatedRoofAndCeilingPlanes,
  getCalculatedInsertionPlane,
  getPickingPlaneInfo,
  getCurrentlyPickablePlaneGuids,
  getHighlightedAbsoluteVerticalPlane,
  getActiveLayoutLevelGuidOrNull,
  getVisibleRoofPlaneGuids,
  getVisibleCeilingPlaneGuids,

  (
    calculatedPlanesDictionary,
    insertionPlane,
    pickingPlaneInfo,
    pickablePlaneGuids,
    highlightedAbsoluteVerticalPlane,
    activeLevelGuid,
    visibleRoofPlaneGuids,
    visibleCeilingPlaneGuids,
  ): DisplayablePlaneWithCalculatedGeometry[] => {
    calculatedPlanesDictionary = produce(calculatedPlanesDictionary, (draft) => {
      mutablyAddHoveredCutToPlaneIfApplicable(draft, pickingPlaneInfo, pickablePlaneGuids);
    });

    const result = Object.values(calculatedPlanesDictionary)
      .filter(notNullish)
      .filter((plane) => visibleRoofPlaneGuids.has(plane.guid) || visibleCeilingPlaneGuids.has(plane.guid));

    if (insertionPlane) {
      result.push(insertionPlane);
    }

    if (highlightedAbsoluteVerticalPlane) {
      const verticalRoofPlane: DisplayablePlane<RoofPlane> = {
        levelGuid: activeLevelGuid,
        flipped: false,
        geometry: { ...highlightedAbsoluteVerticalPlane, type: 'Absolute', elevation: 0 },
        slopeInDegrees: 90,
        heelSettings: DEFAULT_HEEL_SETTINGS,
        deckingThickness: 0,
        topChordWidth: 3.5,
        overhang: 0,
        subfasciaThickness: 0,
        cantilever: 0,
        cuts: [],
      };

      return [
        ...result,
        {
          ...verticalRoofPlane,
          calculatedGeometry: calculateAbsoluteVerticalCutPlaneGeometry(verticalRoofPlane),
        },
      ];
    }

    return result;
  },
);

function mutablyAddHoveredCutToPlaneIfApplicable(
  calculatedPlanesDictionary: Draft<ReadonlyRecord<string, PlaneWithCalculatedGeometry | null>>,
  pickingPlaneInfo: PickingPlaneInfo | undefined,
  pickablePlaneGuids: Set<string>,
) {
  if (!pickingPlaneInfo) return;
  const { targetInfo } = pickingPlaneInfo;
  if (targetInfo.type !== 'roof-cut' && targetInfo.type !== 'ceiling-cut') return;

  const planeToCut = calculatedPlanesDictionary[targetInfo.planeGuidToCut];
  if (!planeToCut) return;

  switch (pickingPlaneInfo.activeMode) {
    case 'real':
      if (
        pickingPlaneInfo.hoveredPlane == null ||
        pickingPlaneInfo.hoveredPlane.type === 'absoluteVertical' ||
        !pickablePlaneGuids.has(pickingPlaneInfo.hoveredPlane.guid)
      )
        return;

      planeToCut.cuts.push({ type: 'AgainstPlane', cuttingPlaneGuid: pickingPlaneInfo.hoveredPlane.guid });

      break;

    case 'absoluteVertical':
      if (pickingPlaneInfo.hoveredPlane?.type === 'absoluteVertical') {
        const hoveredPlane = pickingPlaneInfo.hoveredPlane.geometry;
        planeToCut.cuts.push({ type: 'AbsoluteVertical', ...hoveredPlane });
      }

      break;

    default:
      assertExhaustiveSwitch(pickingPlaneInfo.activeMode);
  }
}

function getPickablePlaneGuidsDuringCutting(
  planeGuids: Set<string>,
  allPlanes: ReturnType<typeof getCalculatedRoofAndCeilingPlanes>,
  selfGuid: string,
) {
  const roofPlaneBeingCut = allPlanes[selfGuid];
  const roofPlaneGeometry = roofPlaneBeingCut && convertPlaneToGeometricPlane(roofPlaneBeingCut, 'Bottom');
  const lastNonSelfCut = roofPlaneBeingCut?.cuts.findLast(
    (cut) => cut.type !== 'AgainstPlane' || cut.cuttingPlaneGuid !== selfGuid,
  );
  const lastCutPlane = lastNonSelfCut
    ? planeCutToPlane(lastNonSelfCut, allPlanes)
    : roofPlaneBeingCut && calculateCutPlaneAtBaseLine(roofPlaneBeingCut);
  const lastCutLine = roofPlaneGeometry && lastCutPlane?.intersectWithPlane(roofPlaneGeometry);

  if (!lastCutLine) return planeGuids;

  const pickableGuids = Array.from(planeGuids).filter((roofPlaneGuid) => {
    const roofPlane = allPlanes[roofPlaneGuid];
    const roofPlaneAsPlane = roofPlane && convertPlaneToGeometricPlane(roofPlane, 'Bottom');
    return roofPlaneAsPlane && !roofPlaneAsPlane.halfSpace.direction.isPerpendicularTo(lastCutLine.direction);
  });

  return new Set(pickableGuids);
}
